"use client"

import Head from "next/head"
import Link from "next/link"
import HrefLang from "@/components/HrefLang"
import * as amplitude from "@amplitude/analytics-browser"
import { useTranslations } from "next-intl"
import { useRouter } from "next/router"
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"
import { useState } from "react"

function SignupPage() {
  const t = useTranslations("TrialPage")
  const title = t("Meta.title")
  const description = t("Meta.description")
  const router = useRouter()
  const { locale } = router
  const { query } = router
  const { page } = query
  const [isAccordionOpen, setIsAccordionOpen] = useState(true)

  const subdomain =
    {
      "zh-TW": "tw.",
      "ja-JP": "jp.",
    }[locale] || ""

  let callbackUri = `https://${subdomain}${process.env.NEXT_PUBLIC_WEB_DOMAIN_NAME}/google/callback`

  if (page == "keyword") {
    callbackUri = callbackUri + "?page=keyword"
  }
  if (page == "anonymous-post") {
    callbackUri = callbackUri + "?page=anonymous-post"
  }

  const url = `${process.env.NEXT_PUBLIC_GOOGLE_CALLBACK_URI}&redirect_uri=${callbackUri}`

  const handleButtonClicked = () => {
    amplitude.logEvent("SignupPage_GoogleLogin_Clicked")
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <HrefLang />
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="og:description" content={description} />
        <meta name="description" content={description} />
      </Head>

      <section className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          {/* Main heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {t("Title.signUpToView")}
          </h1>

          {/* Divider */}
          <div className="w-24 h-1 bg-red-500 mx-auto mb-8"></div>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto leading-relaxed">
            {t("Message.pleaseSignUpFirst1")}
            <br />
            <span className="font-medium text-gray-800">
              {t("Message.noCreditCardRequired")}
            </span>
          </p>

          {/* Google Login Button */}
          <Link href={url} className="block mb-6">
            <Button
              size="lg"
              variant="outline"
              className="w-full border-gray-300 hover:bg-gray-50 text-lg py-6"
              onClick={handleButtonClicked}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t("Action.loginWithGoogle")}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}

export async function getServerSideProps(context) {
  return {
    props: {
      messages: (await import(`@/messages/${context.locale}.json`)).default,
    },
  }
}

export default SignupPage
